import type { ReactNode } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { TenantHeader } from './TenantHeader';
import { SiteFooter } from './SiteFooter';
import { selectBranding, useTenantStore } from '../stores/tenant';

/**
 * Routes whose job is the map. They drop the reading-width container and most
 * of the page padding: the drawing surface competes with two side panels, so
 * every pixel the chrome gives back goes to the map.
 */
const MAP_ROUTES = ['/map', '/coi'];

/**
 * Layout: branded header + main content region + footer.
 * The <title> is set per-page via document.title in each page; the header
 * brand variables cascade to everything inside <main>.
 */
export function Layout(): JSX.Element {
  // Apply tenant brand variables at the layout root (not just the header)
  // so page content — e.g. the landing hero — can use tenant-primary too.
  const tenant = useTenantStore((s) => s.tenant);
  const { primary, accent } = selectBranding(tenant);
  const isMapRoute = MAP_ROUTES.includes(useLocation().pathname);
  return (
    <div
      className="flex min-h-full flex-col"
      style={
        {
          '--tenant-primary': primary,
          '--tenant-accent': accent,
          '--tenant-on-primary': '#ffffff',
        } as React.CSSProperties
      }
    >
      <TenantHeader />
      <main
        className={
          isMapRoute
            ? 'w-full flex-1 p-3'
            : 'mx-auto w-full max-w-7xl flex-1 px-4 py-8 sm:px-6 lg:px-8'
        }
      >
        <Outlet />
      </main>
      {!isMapRoute && <SiteFooter />}
    </div>
  );
}

export function PageShell(props: { children: ReactNode; title: string }): JSX.Element {
  return (
    <div>
      <h1 className="text-3xl font-extrabold tracking-tight text-ink">{props.title}</h1>
      <div className="mt-4">{props.children}</div>
    </div>
  );
}
